import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ConfigService } from '../../../services/config.service';
import {
  LanguageOption,
  TranslationService,
} from '../../../services/translation-service.service';
import { BrandLogoComponent } from '../../../shared/brand-logo/brand-logo.component';
import { TiktokComingSoonComponent } from '../../../shared/tiktok-coming-soon/tiktok-coming-soon.component';
import { ThemeService } from '../../../ui/theme.service';

// ─── Types locaux ───────────────────────────────────────────────────────
interface QuickQuestion {
  id: number;
  questionKey: string;
  answerKey: string;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ContactInfo {
  email: string;
  phone: string;
  website: string;
  hoursKey: string;
}

interface LandingFeature {
  icon: string;
  tone: 'primary' | 'accent' | 'violet' | 'success' | 'warning' | 'pink' | 'tiktok';
  titleKey: string;
  descKey: string;
  comingSoon?: boolean;
}

interface LandingStep {
  icon: string;
  titleKey: string;
  descKey: string;
  illustration: 'wizard' | 'ai' | 'chat' | 'rocket';
}

interface LandingTestimonial {
  quoteKey: string;
  nameKey: string;
  roleKey: string;
  rating: number;
  avatarBg: string;
  avatarFg: string;
}

interface LandingPlan {
  id: 'starter' | 'pro' | 'business';
  nameKey: string;
  priceKey: string;
  periodKey: string;
  featuresKeys: string[];
  ctaKey: string;
  ctaRoute: string;
  popular: boolean;
}

interface LandingFaq {
  qKey: string;
  aKey: string;
  open: WritableSignal<boolean>;
}

interface DemoMessage {
  role: 'user' | 'ai';
  text: string;
  typedLen: number;
  full: string;
}

interface DemoSuggestion {
  key: 'color' | 'promo' | 'hero';
  labelKey: string;
}

/** Hotspot interactif ancré sur la vidéo « ciné-CTA » (fenêtre temporelle). */
interface CinemaHotspot {
  id: string;
  icon: string;
  labelKey: string;
  /** Position en % du cadre vidéo. */
  x: number;
  y: number;
  /** Fenêtre de visibilité (secondes dans la boucle vidéo). */
  from: number;
  to: number;
  /** Action : route interne OU scroll vers une section. */
  route?: string;
  section?: string;
}

/** Chapitre cliquable de la timeline vidéo (seek). */
interface CinemaChapter {
  labelKey: string;
  at: number; // seconde de début
  icon: string;
}

interface TrustLogo {
  name: string;
  icon: string;
}

/** Produit fictif de la vitrine simulée (visuels générés AtlasCloud). */
interface MockProduct {
  img: string;
  nameKey: string;
  price: string;
}

/** Spot Reels de la campagne sociale (fichiers déjà servis /guides/commercial). */
interface ReelSpot {
  day: number;
  titleKey: string;
  captionKey: string;
}

@Component({
  selector: 'app-welcome',
  imports: [RouterModule, CommonModule, BrandLogoComponent, TiktokComingSoonComponent],
  standalone: true,
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.css'],
})
export class WelcomeComponent implements OnInit, OnDestroy, AfterViewInit {
  // ─── État chatbot (préservé) ──────────────────────────────────────────
  readonly isChatbotOpen = signal<boolean>(false);
  readonly showQuickQuestions = signal<boolean>(true);
  readonly showContactInfo = signal<boolean>(false);
  readonly isTyping = signal<boolean>(false);
  readonly messages = signal<ChatMessage[]>([]);

  // ─── Chat LIVE (bot marketing Vendy, same-origin /site/platform/chat) ──
  readonly chatDraft = signal<string>('');
  /** Gate lead : formulaire coordonnées avant tout message live. */
  readonly chatIdentified = signal<boolean>(false);
  readonly chatIdentityBusy = signal<boolean>(false);
  readonly chatIdentityError = signal<string>('');
  readonly visitorName = signal<string>('');
  readonly visitorEmail = signal<string>('');
  readonly visitorPhone = signal<string>('');
  private chatSessionId = '';
  private chatLastId = 0;
  private chatLiveStarted = false;
  private chatPollTimer: ReturnType<typeof setTimeout> | null = null;
  private chatTypingGuard: ReturnType<typeof setTimeout> | null = null;
  private readonly chatVisitorKey = 'vd_chat_visitor_platform';
  private readonly chatSessionKey = 'vd_lp_chat_session';

  // ─── Quick questions (i18n via translate()) ───────────────────────────
  readonly quickQuestions: QuickQuestion[] = [
    { id: 1, questionKey: 'lp_chatbot_q1', answerKey: 'lp_chatbot_a1' },
    { id: 2, questionKey: 'lp_chatbot_q2', answerKey: 'lp_chatbot_a2' },
    { id: 3, questionKey: 'lp_chatbot_q3', answerKey: 'lp_chatbot_a3' },
    { id: 4, questionKey: 'lp_chatbot_q4', answerKey: 'lp_chatbot_a4' },
    { id: 5, questionKey: 'lp_chatbot_q5', answerKey: 'lp_chatbot_a5' },
    { id: 6, questionKey: 'lp_chatbot_q6', answerKey: 'lp_chatbot_a6' },
  ];

  contactInfo!: ContactInfo;

  // ─── i18n / thème ─────────────────────────────────────────────────────
  currentLanguage: LanguageOption;
  availableLanguages: LanguageOption[] = [];
  readonly isDark = computed(() => this.theme.isDark());

  // ─── Landing : header / scroll / mobile ───────────────────────────────
  readonly isScrolled = signal<boolean>(false);
  readonly mobileMenuOpen = signal<boolean>(false);

  // ─── Parallax souris (hero) ───────────────────────────────────────────
  readonly heroParallax = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  // ─── Vidéo de fond hero (générée AtlasCloud — wan-2.5) ────────────────
  /** true quand la vidéo est décodable → fondu du poster vers la vidéo. */
  readonly heroVideoReady = signal<boolean>(false);
  /** Motion réduite (accessibilité) : ni vidéo hero, ni tilt 3D. */
  readonly motionOk = signal<boolean>(true);

  // ─── Ciné-CTA : vidéo interactive 3D avec hotspots cliquables ─────────
  readonly cinemaPlaying = signal<boolean>(false);
  readonly cinemaTime = signal<number>(0);
  readonly cinemaDuration = signal<number>(10);
  readonly cinemaStarted = signal<boolean>(false);
  /** Tilt 3D interactif du cadre (suivi pointeur). */
  readonly cinemaTilt = signal<{ rx: number; ry: number; gx: number; gy: number }>(
    { rx: 0, ry: 0, gx: 50, gy: 50 },
  );

  /** Hotspots CTA ancrés sur la vidéo (fenêtres temporelles de la boucle). */
  readonly cinemaHotspots: CinemaHotspot[] = [
    { id: 'signup', icon: 'rocket_launch', labelKey: 'lp_cinema_hs_signup', x: 24, y: 34, from: 0.4, to: 4.2, route: '/signup' },
    { id: 'inbox', icon: 'forum', labelKey: 'lp_cinema_hs_inbox', x: 72, y: 30, from: 3.4, to: 7.2, section: 'lp-features' },
    { id: 'studio', icon: 'auto_awesome', labelKey: 'lp_cinema_hs_studio', x: 48, y: 72, from: 6.4, to: 10.0, section: 'lp-demo' },
  ];
  readonly cinemaChapters: CinemaChapter[] = [
    { labelKey: 'lp_cinema_chap_1', at: 0, icon: 'storefront' },
    { labelKey: 'lp_cinema_chap_2', at: 3.4, icon: 'forum' },
    { labelKey: 'lp_cinema_chap_3', at: 6.4, icon: 'auto_awesome' },
  ];
  /** Hotspots visibles à l'instant courant de la boucle. */
  readonly visibleHotspots = computed<CinemaHotspot[]>(() => {
    const t = this.cinemaTime();
    if (!this.cinemaStarted()) return [];
    return this.cinemaHotspots.filter((h) => t >= h.from && t <= h.to);
  });
  /** Progression 0..100 de la timeline. */
  readonly cinemaProgress = computed<number>(() => {
    const d = this.cinemaDuration() || 1;
    return Math.min(100, (this.cinemaTime() / d) * 100);
  });

  // ─── Hero mockup IA — conversation simulée ────────────────────────────
  readonly heroMockMessages = signal<DemoMessage[]>([]);
  readonly heroMockColor = signal<string>('#6366f1');
  readonly heroMockBadge = signal<string | null>(null);
  readonly heroMockHero = signal<boolean>(false);

  // ─── Vitrine simulée : produits artisan (visuels AtlasCloud, self-hostés,
  //     i18n) — remplace les skeletons gris des mockups hero/démo. ─────────
  readonly mockHeroProduct: MockProduct = {
    img: '/assets/landing/mock/mock-hero.webp',
    nameKey: 'lp_mock_hero_name',
    price: '189 TND',
  };
  readonly mockProducts: MockProduct[] = [
    { img: '/assets/landing/mock/mock-p1.webp', nameKey: 'lp_mock_p1', price: '65 TND' },
    { img: '/assets/landing/mock/mock-p2.webp', nameKey: 'lp_mock_p2', price: '39 TND' },
    { img: '/assets/landing/mock/mock-p3.webp', nameKey: 'lp_mock_p3', price: '129 TND' },
    { img: '/assets/landing/mock/mock-p4.webp', nameKey: 'lp_mock_p4', price: '95 TND' },
    { img: '/assets/landing/mock/mock-p5.webp', nameKey: 'lp_mock_p5', price: '75 TND' },
    { img: '/assets/landing/mock/mock-p6.webp', nameKey: 'lp_mock_p6', price: '59 TND' },
  ];

  // ─── Reels : 3 spots de la campagne (posters locaux, MP4 servis
  //     /guides/commercial). La langue suit la locale courante. ────────────
  readonly reels: ReelSpot[] = [
    { day: 1, titleKey: 'lp_reel_1_title', captionKey: 'lp_reel_1_caption' },
    { day: 5, titleKey: 'lp_reel_5_title', captionKey: 'lp_reel_5_caption' },
    { day: 7, titleKey: 'lp_reel_7_title', captionKey: 'lp_reel_7_caption' },
  ];
  /** Reel en cours de lecture (day) — un seul à la fois. */
  readonly reelPlaying = signal<number | null>(null);

  reelLang(): string {
    const code = this.currentLanguage.code;
    return code === 'ar' ? 'ar' : code === 'en' ? 'en' : 'fr';
  }

  reelPoster(day: number): string {
    return `/assets/landing/spots/spot-day${day}-${this.reelLang()}.jpg`;
  }

  reelSrc(day: number): string {
    return `/guides/commercial/vendeur-site-day${day}-${this.reelLang()}.mp4`;
  }

  playReel(day: number): void {
    this.reelPlaying.set(this.reelPlaying() === day ? null : day);
  }

  // ─── Stats animées (count-up au scroll) ───────────────────────────────
  readonly stats: Array<{ key: string; target: number; suffix: string; labelKey: string; icon: string }> = [
    { key: 'vendors', target: 50, suffix: '+',   labelKey: 'lp_hero_stat_vendors', icon: 'storefront' },
    { key: 'setup',   target: 2,  suffix: 'min', labelKey: 'lp_hero_stat_setup',   icon: 'timer' },
    { key: 'channels', target: 4, suffix: '',    labelKey: 'lp_feature_inbox_title', icon: 'forum' },
    { key: 'uptime',  target: 99, suffix: '%',   labelKey: 'stats_uptime',         icon: 'verified' },
  ];
  readonly statValues = signal<Record<string, number>>({
    vendors: 0, setup: 0, channels: 0, uptime: 0,
  });
  private statsAnimated = false;

  // ─── Trust logos (placeholders crédibles) ─────────────────────────────
  readonly trustLogos: TrustLogo[] = [
    { name: 'ModeBoutique',  icon: 'checkroom' },
    { name: 'TechMaghreb',   icon: 'memory' },
    { name: 'FoodCorner',    icon: 'restaurant' },
    { name: 'EpicerieXYZ',   icon: 'shopping_basket' },
    { name: 'BeautéTunis',   icon: 'auto_awesome' },
    { name: 'SportHub',      icon: 'sports_basketball' },
    { name: 'ArtisanatBenz', icon: 'palette' },
    { name: 'BioMarket',     icon: 'eco' },
  ];

  // ─── Features ─────────────────────────────────────────────────────────
  readonly features: LandingFeature[] = [
    { icon: 'auto_awesome',   tone: 'primary', titleKey: 'lp_feature_builder_title',    descKey: 'lp_feature_builder_desc' },
    { icon: 'forum',          tone: 'violet',  titleKey: 'lp_feature_inbox_title',      descKey: 'lp_feature_inbox_desc' },
    { icon: 'inventory_2',    tone: 'accent',  titleKey: 'lp_feature_360_title',        descKey: 'lp_feature_360_desc' },
    { icon: 'local_shipping', tone: 'success', titleKey: 'lp_feature_shipping_title',   descKey: 'lp_feature_shipping_desc' },
    { icon: 'insights',       tone: 'warning', titleKey: 'lp_feature_analytics_title',  descKey: 'lp_feature_analytics_desc' },
    { icon: 'history',        tone: 'pink',    titleKey: 'lp_feature_versioning_title', descKey: 'lp_feature_versioning_desc' },
    // TikTok placeholder — conversion driver : on annonce le canal pour
    // capturer l'intention dès la landing publique.
    { icon: 'music_note',     tone: 'tiktok',  titleKey: 'lp_feature_tiktok_title',     descKey: 'lp_feature_tiktok_desc',     comingSoon: true },
  ];

  // ─── Steps ────────────────────────────────────────────────────────────
  readonly steps: LandingStep[] = [
    { icon: 'edit_note',     titleKey: 'lp_step1_title', descKey: 'lp_step1_desc', illustration: 'wizard' },
    { icon: 'psychology',    titleKey: 'lp_step2_title', descKey: 'lp_step2_desc', illustration: 'ai' },
    { icon: 'chat',          titleKey: 'lp_step3_title', descKey: 'lp_step3_desc', illustration: 'chat' },
    { icon: 'rocket_launch', titleKey: 'lp_step4_title', descKey: 'lp_step4_desc', illustration: 'rocket' },
  ];

  // ─── Démo interactive ─────────────────────────────────────────────────
  readonly demoMessages = signal<DemoMessage[]>([]);
  readonly demoColor = signal<string>('#6366f1');
  readonly demoBadge = signal<string | null>(null);
  readonly demoHero = signal<boolean>(false);
  readonly demoSuggestions: DemoSuggestion[] = [
    { key: 'color', labelKey: 'lp_demo_suggestion_color' },
    { key: 'promo', labelKey: 'lp_demo_suggestion_promo' },
    { key: 'hero',  labelKey: 'lp_demo_suggestion_hero' },
  ];

  // ─── Plans ────────────────────────────────────────────────────────────
  readonly plans: LandingPlan[] = [
    {
      id: 'starter',
      nameKey: 'lp_plan_starter_name',
      priceKey: 'lp_plan_starter_price',
      periodKey: 'lp_plan_starter_period',
      featuresKeys: ['lp_plan_starter_f1', 'lp_plan_starter_f2', 'lp_plan_starter_f3', 'lp_plan_starter_f4', 'lp_plan_starter_f5'],
      ctaKey: 'lp_plan_starter_cta',
      ctaRoute: '/signup',
      popular: false,
    },
    {
      id: 'pro',
      nameKey: 'lp_plan_pro_name',
      priceKey: 'lp_plan_pro_price',
      periodKey: 'lp_plan_pro_period',
      featuresKeys: ['lp_plan_pro_f1', 'lp_plan_pro_f2', 'lp_plan_pro_f3', 'lp_plan_pro_f4', 'lp_plan_pro_f5', 'lp_plan_pro_f6'],
      ctaKey: 'lp_plan_pro_cta',
      ctaRoute: '/signup',
      popular: true,
    },
    {
      id: 'business',
      nameKey: 'lp_plan_business_name',
      priceKey: 'lp_plan_business_price',
      periodKey: 'lp_plan_business_period',
      featuresKeys: ['lp_plan_business_f1', 'lp_plan_business_f2', 'lp_plan_business_f3', 'lp_plan_business_f4', 'lp_plan_business_f5'],
      ctaKey: 'lp_plan_business_cta',
      ctaRoute: '/signup',
      popular: false,
    },
  ];

  // ─── Témoignages (carousel) ───────────────────────────────────────────
  readonly testimonials: LandingTestimonial[] = [
    { quoteKey: 'lp_testimonial_1_quote', nameKey: 'lp_testimonial_1_name', roleKey: 'lp_testimonial_1_role', rating: 5, avatarBg: 'linear-gradient(135deg,#ec4899,#f43f5e)', avatarFg: '#fff' },
    { quoteKey: 'lp_testimonial_2_quote', nameKey: 'lp_testimonial_2_name', roleKey: 'lp_testimonial_2_role', rating: 5, avatarBg: 'linear-gradient(135deg,#10b981,#059669)', avatarFg: '#fff' },
    { quoteKey: 'lp_testimonial_3_quote', nameKey: 'lp_testimonial_3_name', roleKey: 'lp_testimonial_3_role', rating: 5, avatarBg: 'linear-gradient(135deg,#06b6d4,#0891b2)', avatarFg: '#fff' },
    { quoteKey: 'lp_testimonial_4_quote', nameKey: 'lp_testimonial_4_name', roleKey: 'lp_testimonial_4_role', rating: 5, avatarBg: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', avatarFg: '#fff' },
    { quoteKey: 'lp_testimonial_5_quote', nameKey: 'lp_testimonial_5_name', roleKey: 'lp_testimonial_5_role', rating: 5, avatarBg: 'linear-gradient(135deg,#f59e0b,#d97706)', avatarFg: '#fff' },
  ];
  readonly testimonialIndex = signal<number>(0);
  readonly visibleTestimonials = computed<LandingTestimonial[]>(() => {
    const i = this.testimonialIndex();
    const n = this.testimonials.length;
    return [
      this.testimonials[(i) % n],
      this.testimonials[(i + 1) % n],
      this.testimonials[(i + 2) % n],
    ];
  });

  // ─── FAQ ──────────────────────────────────────────────────────────────
  readonly faqs: LandingFaq[] = [
    { qKey: 'lp_faq_1_q', aKey: 'lp_faq_1_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_2_q', aKey: 'lp_faq_2_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_3_q', aKey: 'lp_faq_3_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_4_q', aKey: 'lp_faq_4_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_5_q', aKey: 'lp_faq_5_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_6_q', aKey: 'lp_faq_6_a', open: signal<boolean>(false) },
    { qKey: 'lp_faq_7_q', aKey: 'lp_faq_7_a', open: signal<boolean>(false) },
  ];

  // ─── Confetti (1 fois au scroll passé hero) ───────────────────────────
  readonly showConfetti = signal<boolean>(false);
  readonly confettiParticles: number[] = [0, 1, 2, 3, 4, 5, 6, 7];
  private confettiTriggered = false;

  // ─── Internals ────────────────────────────────────────────────────────
  private readonly destroy$ = new Subject<void>();
  private observers: IntersectionObserver[] = [];
  private heroMockInterval?: ReturnType<typeof setInterval>;
  private testimonialInterval?: ReturnType<typeof setInterval>;
  private demoTypingTimers: Array<ReturnType<typeof setTimeout>> = [];
  private reducedMotion = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public translationService: TranslationService,
    public theme: ThemeService,
    private configService: ConfigService,
    private elementRef: ElementRef<HTMLElement>,
    private router: Router,
  ) {
    this.contactInfo = {
      email: 'vendeur.site@codincloud.com',
      phone: '+216 25 507 800',
      website: this.configService?.websiteUrl || 'https://vendeur.site',
      hoursKey: 'lp_chatbot_contact_hours_val',
    };

    this.currentLanguage = this.translationService.availableLanguages[0];
    this.availableLanguages = this.translationService.availableLanguages;
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────
  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.motionOk.set(!this.reducedMotion);

      // Session opaque du chat live (persistée : le prospect retrouve son fil)
      try {
        this.chatSessionId = localStorage.getItem(this.chatSessionKey) || this.readCookie(this.chatSessionKey) || '';
        if (!this.chatSessionId) {
          this.chatSessionId = (crypto as any)?.randomUUID?.()
            || `lp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }
        localStorage.setItem(this.chatSessionKey, this.chatSessionId);
        this.writeCookie(this.chatSessionKey, this.chatSessionId, 365);
        this.restoreVisitorFromStorage();
        this.syncIdentityWithServer();
      } catch { /* localStorage indisponible : chat live désactivé */ }
    }

    this.translationService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.currentLanguage = language;
        if (isPlatformBrowser(this.platformId)) {
          document.body.classList.toggle('rtl', language.direction === 'rtl');
        }
      });

    if (!this.reducedMotion && isPlatformBrowser(this.platformId)) {
      this.startHeroMockConversation();
      this.startTestimonialRotation();
    } else {
      this.stats.forEach((s) => this.statValues.update((v) => ({ ...v, [s.key]: s.target })));
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Scroll reveal observer
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            revealObserver.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );
    this.elementRef.nativeElement.querySelectorAll('.lp-reveal').forEach((el) => revealObserver.observe(el));
    this.observers.push(revealObserver);

    // Count-up observer (déclenché 1× pour la section stats)
    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !this.statsAnimated) {
            this.statsAnimated = true;
            this.runCountUp();
            statsObserver.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    const statsEl = this.elementRef.nativeElement.querySelector('.lp-stat-row');
    if (statsEl) statsObserver.observe(statsEl);
    this.observers.push(statsObserver);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.heroMockInterval) clearInterval(this.heroMockInterval);
    if (this.testimonialInterval) clearInterval(this.testimonialInterval);
    this.demoTypingTimers.forEach((t) => clearTimeout(t));
    this.demoTypingTimers = [];
    this.stopChatPolling();
  }

  // ─── Listeners ────────────────────────────────────────────────────────
  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const y = window.scrollY || 0;
    this.isScrolled.set(y > 30);

    if (!this.confettiTriggered && !this.reducedMotion && y > window.innerHeight * 0.6) {
      this.confettiTriggered = true;
      this.showConfetti.set(true);
      setTimeout(() => this.showConfetti.set(false), 3200);
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId) || this.reducedMotion) return;
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    this.heroParallax.set({
      x: ((e.clientX / w) - 0.5) * 12,
      y: ((e.clientY / h) - 0.5) * 12,
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.mobileMenuOpen()) this.closeMobileMenu();
  }

  // ─── Theme / Lang / Menu ──────────────────────────────────────────────
  toggleTheme(): void {
    this.theme.toggle();
  }

  changeLanguage(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.translationService.setLanguage(select.value);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update((v) => !v);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  scrollToSection(id: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.closeMobileMenu();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  // ─── Ciné-CTA : vidéo interactive (hotspots + tilt 3D + chapitres) ────
  onCinemaMeta(video: HTMLVideoElement): void {
    if (video.duration && isFinite(video.duration)) {
      this.cinemaDuration.set(video.duration);
    }
  }

  onCinemaTime(video: HTMLVideoElement): void {
    this.cinemaTime.set(video.currentTime || 0);
  }

  /** Clic sur la vidéo elle-même : lance ou met en pause (CTA interactif). */
  toggleCinema(video: HTMLVideoElement): void {
    if (video.paused) {
      video.muted = true; // autoplay policy : lecture programmatique muette
      void video.play().catch(() => undefined);
      this.cinemaPlaying.set(true);
      this.cinemaStarted.set(true);
    } else {
      video.pause();
      this.cinemaPlaying.set(false);
    }
  }

  /** Action d'un hotspot cliqué SUR la vidéo : route ou scroll section. */
  cinemaHotspotAction(h: CinemaHotspot, ev: Event): void {
    ev.stopPropagation();
    if (h.route) {
      void this.router.navigateByUrl(h.route);
      return;
    }
    if (h.section) this.scrollToSection(h.section);
  }

  /** Chapitre cliqué : seek + lecture. */
  seekChapter(video: HTMLVideoElement, at: number, ev: Event): void {
    ev.stopPropagation();
    try {
      video.currentTime = Math.max(0, Math.min(at, (video.duration || 10) - 0.05));
    } catch {
      /* seek best-effort */
    }
    if (video.paused) this.toggleCinema(video);
    this.cinemaStarted.set(true);
  }

  /** Tilt 3D interactif : le cadre suit le pointeur (désactivé reduced-motion). */
  onCinemaMove(ev: MouseEvent, host: HTMLElement): void {
    if (this.reducedMotion) return;
    const r = host.getBoundingClientRect();
    const px = (ev.clientX - r.left) / Math.max(1, r.width);
    const py = (ev.clientY - r.top) / Math.max(1, r.height);
    this.cinemaTilt.set({
      rx: (0.5 - py) * 10,
      ry: (px - 0.5) * 14,
      gx: px * 100,
      gy: py * 100,
    });
  }

  onCinemaLeave(): void {
    this.cinemaTilt.set({ rx: 0, ry: 0, gx: 50, gy: 50 });
  }

  // ─── Hero mock conversation (typewriter) ──────────────────────────────
  private startHeroMockConversation(): void {
    // i18n : les répliques sont résolues à CHAQUE pas (le visiteur peut
    // changer de langue pendant la boucle — plus de français figé en AR/EN).
    const seq: Array<{ role: 'user' | 'ai'; key: string; applyColor?: string; applyBadge?: string; applyHero?: boolean }> = [
      { role: 'user', key: 'lp_demo_user_color' },
      { role: 'ai',   key: 'lp_demo_ai_color', applyColor: '#8b5cf6' },
      { role: 'user', key: 'lp_demo_user_promo' },
      { role: 'ai',   key: 'lp_demo_ai_promo', applyBadge: '-10%' },
      { role: 'user', key: 'lp_demo_user_hero' },
      { role: 'ai',   key: 'lp_demo_ai_hero', applyHero: true },
    ];

    const playStep = (idx: number) => {
      if (idx >= seq.length) {
        // reset après une pause
        setTimeout(() => {
          this.heroMockMessages.set([]);
          this.heroMockColor.set('#6366f1');
          this.heroMockBadge.set(null);
          this.heroMockHero.set(false);
          playStep(0);
        }, 3500);
        return;
      }
      const step = seq[idx];
      const msg: DemoMessage = { role: step.role || 'ai', text: '', typedLen: 0, full: this.translate(step.key) };
      this.heroMockMessages.update((m) => [...m.slice(-3), msg]);
      this.typewriter(msg, () => {
        if (step.applyColor) this.heroMockColor.set(step.applyColor);
        if (step.applyBadge) this.heroMockBadge.set(step.applyBadge);
        if (step.applyHero != null) this.heroMockHero.set(!!step.applyHero);
        setTimeout(() => playStep(idx + 1), 1600);
      });
    };

    playStep(0);
  }

  private typewriter(msg: DemoMessage, done: () => void): void {
    const interval = 30;
    const tick = () => {
      if (msg.typedLen < msg.full.length) {
        msg.typedLen++;
        msg.text = msg.full.substring(0, msg.typedLen);
        this.heroMockMessages.update((arr) => [...arr]);
        const t = setTimeout(tick, interval);
        this.demoTypingTimers.push(t);
      } else {
        done();
      }
    };
    tick();
  }

  // ─── Démo interactive (clics utilisateur) ─────────────────────────────
  runDemo(key: 'color' | 'promo' | 'hero'): void {
    const userKey = `lp_demo_user_${key}`;
    const aiKey   = `lp_demo_ai_${key}`;

    const userMsg: DemoMessage = { role: 'user', text: this.translate(userKey), typedLen: this.translate(userKey).length, full: this.translate(userKey) };
    const aiMsg:   DemoMessage = { role: 'ai',   text: '', typedLen: 0, full: this.translate(aiKey) };
    this.demoMessages.update((m) => [...m, userMsg, aiMsg]);

    // Type ai answer + apply effect when complete
    if (this.reducedMotion) {
      aiMsg.text = aiMsg.full;
      aiMsg.typedLen = aiMsg.full.length;
      this.demoMessages.update((arr) => [...arr]);
      this.applyDemoEffect(key);
      return;
    }

    const interval = 28;
    const tick = () => {
      if (aiMsg.typedLen < aiMsg.full.length) {
        aiMsg.typedLen++;
        aiMsg.text = aiMsg.full.substring(0, aiMsg.typedLen);
        this.demoMessages.update((arr) => [...arr]);
        const t = setTimeout(tick, interval);
        this.demoTypingTimers.push(t);
      } else {
        this.applyDemoEffect(key);
      }
    };
    setTimeout(tick, 250);
  }

  private applyDemoEffect(key: 'color' | 'promo' | 'hero'): void {
    if (key === 'color') this.demoColor.set('#06b6d4');
    else if (key === 'promo') this.demoBadge.set('-10%');
    else if (key === 'hero') this.demoHero.set(true);
  }

  resetDemo(): void {
    this.demoMessages.set([]);
    this.demoColor.set('#6366f1');
    this.demoBadge.set(null);
    this.demoHero.set(false);
  }

  // ─── Stats count-up ───────────────────────────────────────────────────
  private runCountUp(): void {
    const duration = 1400;
    const startTs = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTs;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const map: Record<string, number> = {};
      this.stats.forEach((s) => (map[s.key] = Math.round(s.target * eased)));
      this.statValues.set(map);
      if (t < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  // ─── Témoignages auto-scroll ──────────────────────────────────────────
  private startTestimonialRotation(): void {
    this.testimonialInterval = setInterval(() => {
      this.testimonialIndex.update((i) => (i + 1) % this.testimonials.length);
    }, 7000);
  }

  prevTestimonial(): void {
    this.testimonialIndex.update((i) => (i - 1 + this.testimonials.length) % this.testimonials.length);
  }
  nextTestimonial(): void {
    this.testimonialIndex.update((i) => (i + 1) % this.testimonials.length);
  }

  // ─── FAQ ──────────────────────────────────────────────────────────────
  toggleFaq(faq: LandingFaq): void {
    faq.open.set(!faq.open());
  }

  // ─── Chatbot (logique préservée, nouveau wrapper signal) ──────────────
  toggleChatbot(): void {
    const next = !this.isChatbotOpen();
    this.isChatbotOpen.set(next);
    if (next && this.messages().length === 0 && this.chatIdentified()) {
      setTimeout(() => this.addBotMessage(this.translate('lp_chatbot_welcome')), 350);
    }
    if (next && this.chatIdentified()) {
      this.loadChatHistory();
      if (this.chatLiveStarted) this.scheduleChatPoll(600);
    }
    if (!next) {
      this.stopChatPolling();
    }
  }

  // ─── Gate lead : coordonnées avant chat live ─────────────────────────
  submitVisitorIdentity(): void {
    if (!isPlatformBrowser(this.platformId) || !this.chatSessionId) return;
    const name = this.visitorName().trim();
    const email = this.visitorEmail().trim();
    const phone = this.visitorPhone().trim();
    if (name.length < 2) {
      this.chatIdentityError.set('Indiquez votre nom.');
      return;
    }
    if (!email && !phone) {
      this.chatIdentityError.set('Email ou téléphone requis.');
      return;
    }
    this.chatIdentityError.set('');
    this.chatIdentityBusy.set(true);
    fetch('/site/platform/chat/identify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: this.chatSessionId,
        visitor_name: name,
        visitor_email: email || null,
        visitor_phone: phone || null,
        page_url: typeof location !== 'undefined' ? location.href : undefined,
      }),
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j?.ok) {
          const detail = typeof j?.detail === 'string' ? j.detail : 'Impossible d’enregistrer vos coordonnées.';
          this.chatIdentityError.set(detail);
          return;
        }
        this.persistVisitor({ name, email, phone });
        this.chatIdentified.set(true);
        if (this.messages().length === 0) {
          this.addBotMessage(this.translate('lp_chatbot_welcome'));
        }
        this.loadChatHistory();
      })
      .catch(() => this.chatIdentityError.set('Erreur réseau. Réessayez.'))
      .finally(() => this.chatIdentityBusy.set(false));
  }

  private restoreVisitorFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.chatVisitorKey) || this.readCookie(this.chatVisitorKey);
      if (!raw) return;
      const o = JSON.parse(raw);
      if (o?.name && (o.email || o.phone)) {
        this.visitorName.set(o.name || '');
        this.visitorEmail.set(o.email || '');
        this.visitorPhone.set(o.phone || '');
      }
    } catch { /* ignore */ }
  }

  private persistVisitor(v: { name: string; email: string; phone: string }): void {
    try {
      const payload = JSON.stringify(v);
      localStorage.setItem(this.chatVisitorKey, payload);
      this.writeCookie(this.chatVisitorKey, payload, 365);
    } catch { /* ignore */ }
  }

  private syncIdentityWithServer(): void {
    if (!this.chatSessionId) return;
    fetch(`/site/platform/chat/config?session_id=${encodeURIComponent(this.chatSessionId)}`)
      .then((r) => r.json())
      .then((c) => {
        if (c?.identified) {
          this.chatIdentified.set(true);
          if (c.visitor_name) this.visitorName.set(c.visitor_name);
          if (c.visitor_email) this.visitorEmail.set(c.visitor_email);
          if (c.visitor_phone) this.visitorPhone.set(c.visitor_phone);
          this.persistVisitor({
            name: this.visitorName(),
            email: this.visitorEmail(),
            phone: this.visitorPhone(),
          });
          return;
        }
        // Cookie local → re-sync idempotent
        const name = this.visitorName().trim();
        const email = this.visitorEmail().trim();
        const phone = this.visitorPhone().trim();
        if (name && (email || phone)) {
          fetch('/site/platform/chat/identify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: this.chatSessionId,
              visitor_name: name,
              visitor_email: email || null,
              visitor_phone: phone || null,
              page_url: typeof location !== 'undefined' ? location.href : undefined,
            }),
          })
            .then((r) => r.json())
            .then((j) => { if (j?.ok) this.chatIdentified.set(true); })
            .catch(() => { /* best-effort */ });
        }
      })
      .catch(() => { /* best-effort */ });
  }

  private loadChatHistory(): void {
    if (!this.chatSessionId || !this.chatIdentified()) return;
    fetch(`/site/platform/chat/messages?session_id=${encodeURIComponent(this.chatSessionId)}&after_id=0`)
      .then((r) => r.json())
      .then((data: { messages?: Array<{ id: number; direction: string; sender_type: string; text: string }> }) => {
        const list = data.messages || [];
        if (!list.length) return;
        this.messages.set([]);
        this.chatLastId = 0;
        for (const m of list) {
          if (m.id > this.chatLastId) this.chatLastId = m.id;
          if (!m.text) continue;
          if (m.direction === 'in' && m.sender_type === 'customer') {
            this.addUserMessage(m.text);
          } else {
            this.addBotMessage(m.text);
          }
        }
        this.chatLiveStarted = true;
        this.showQuickQuestions.set(false);
      })
      .catch(() => { /* ignore */ });
  }

  private writeCookie(name: string, value: string, days: number): void {
    try {
      const exp = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${exp}; SameSite=Lax`;
    } catch { /* ignore */ }
  }

  private readCookie(name: string): string {
    try {
      const parts = (`; ${document.cookie}`).split(`; ${name}=`);
      if (parts.length === 2) return decodeURIComponent(parts.pop()?.split(';').shift() || '');
    } catch { /* ignore */ }
    return '';
  }

  // ─── Chat LIVE : envoi au bot marketing (same-origin, sans intercepteur) ─
  sendLiveMessage(): void {
    const text = this.chatDraft().trim();
    if (!text || !isPlatformBrowser(this.platformId) || !this.chatSessionId) return;
    if (!this.chatIdentified()) {
      this.chatIdentityError.set('Identifiez-vous avant d’envoyer un message.');
      return;
    }
    this.chatDraft.set('');
    this.addUserMessage(text);
    this.showQuickQuestions.set(false);
    this.showContactInfo.set(false);
    this.isTyping.set(true);
    this.chatLiveStarted = true;

    fetch('/site/platform/chat/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: this.chatSessionId,
        text,
        page_url: typeof location !== 'undefined' ? location.href : undefined,
        visitor_name: this.visitorName() || undefined,
        visitor_email: this.visitorEmail() || undefined,
        visitor_phone: this.visitorPhone() || undefined,
      }),
    })
      .then((r) => {
        if (r.status === 403) {
          this.chatIdentified.set(false);
          this.isTyping.set(false);
          this.chatIdentityError.set('Identifiez-vous avant d’envoyer un message.');
        }
      })
      .catch(() => { /* le polling ne montrera rien : garde-fou ci-dessous */ });

    this.scheduleChatPoll(1500);
    if (this.chatTypingGuard) clearTimeout(this.chatTypingGuard);
    this.chatTypingGuard = setTimeout(() => {
      if (this.isTyping()) {
        this.isTyping.set(false);
        this.addBotMessage(this.translate('lp_chatbot_live_slow'));
      }
    }, 90_000);
  }

  private scheduleChatPoll(delayMs: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.chatPollTimer) clearTimeout(this.chatPollTimer);
    this.chatPollTimer = setTimeout(() => this.pollLiveMessages(), delayMs);
  }

  private stopChatPolling(): void {
    if (this.chatPollTimer) { clearTimeout(this.chatPollTimer); this.chatPollTimer = null; }
    if (this.chatTypingGuard) { clearTimeout(this.chatTypingGuard); this.chatTypingGuard = null; }
  }

  private pollLiveMessages(): void {
    if (!this.isChatbotOpen() || !this.chatLiveStarted) return;
    const url = `/site/platform/chat/messages?session_id=${encodeURIComponent(this.chatSessionId)}&after_id=${this.chatLastId}`;
    fetch(url)
      .then((r) => r.json())
      .then((data: { messages?: Array<{ id: number; direction: string; sender_type: string; text: string }> }) => {
        for (const m of data.messages || []) {
          if (m.id > this.chatLastId) this.chatLastId = m.id;
          // Nos propres messages (optimistes) : ne pas doublonner.
          if (m.direction === 'in' && m.sender_type === 'customer') continue;
          if (!m.text) continue;
          this.isTyping.set(false);
          if (this.chatTypingGuard) { clearTimeout(this.chatTypingGuard); this.chatTypingGuard = null; }
          this.addBotMessage(m.text);
        }
      })
      .catch(() => { /* réseau : on retentera au tick suivant */ })
      .finally(() => {
        if (this.isChatbotOpen() && this.chatLiveStarted) this.scheduleChatPoll(3500);
      });
  }

  handleQuestionClick(question: QuickQuestion): void {
    this.addUserMessage(this.translate(question.questionKey));
    this.showQuickQuestions.set(false);
    this.isTyping.set(true);
    setTimeout(() => {
      this.addBotMessage(this.translate(question.answerKey));
      this.isTyping.set(false);
    }, 1100);
  }

  toggleQuickQuestions(): void {
    this.showQuickQuestions.update((v) => !v);
  }

  showContact(): void {
    this.showContactInfo.set(true);
    this.showQuickQuestions.set(false);
    this.addBotMessage(this.translate('lp_chatbot_contact_title'));
  }

  resetChat(): void {
    this.messages.set([]);
    this.showQuickQuestions.set(true);
    this.showContactInfo.set(false);
    this.isTyping.set(false);
  }

  switchToChatMode(): void {
    this.showQuickQuestions.set(true);
    this.showContactInfo.set(false);
  }

  private addUserMessage(text: string): void {
    this.messages.update((arr) => [...arr, { id: Date.now(), text, sender: 'user', timestamp: new Date() }]);
    this.scrollChatToBottom();
  }

  private addBotMessage(text: string): void {
    this.messages.update((arr) => [...arr, { id: Date.now() + 1, text, sender: 'bot', timestamp: new Date() }]);
    this.scrollChatToBottom();
  }

  private scrollChatToBottom(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => {
      const el = document.getElementById('lp-chat-messages');
      if (el) el.scrollTop = el.scrollHeight;
    }, 60);
  }

  formatTime(date: Date): string {
    try {
      const locale = this.currentLanguage?.code === 'ar' ? 'ar-TN' : this.currentLanguage?.code === 'en' ? 'en-US' : 'fr-FR';
      return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────
  translate(key: string): string {
    return this.translationService.translate(key);
  }

  trackByIndex(index: number): number { return index; }

  // Helper TS pour template (étoiles d'avis)
  starsArray(rating: number): number[] {
    const n = Math.max(0, Math.min(5, Math.floor(rating)));
    return Array.from({ length: n });
  }
}
